var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/**
 * Which destination a recording goes to.
 *
 * This is the whole of the transport decision, in one place, so the two
 * interview contexts stay orchestration layers and cannot drift apart the way
 * their recorders once did.
 *
 *                       SharedRecorder
 *                             │
 *                   ┌─────────┴─────────┐
 *              flag off              flag on
 *                   │                   │
 *        LegacyWebSocketSink      S3MultipartSink
 *        (base64 over the WS)     (direct browser -> S3)
 *
 * DEFAULT OFF, AND FAIL-SAFE. Anything that goes wrong — the config endpoint is
 * unreachable, the control plane refuses to start an upload, the build forces
 * it off — lands on the legacy path. An unreachable backend is the worst
 * possible moment to start depending on a new one, and a recording that takes
 * the old route is far better than an interview that fails.
 */
import { createPendingChunkStore } from './pending-chunks';
import { shouldUseMultipart } from '../context/recording-config';
import { LegacyWebSocketSink } from './legacy-websocket-sink';
import { S3MultipartSink, createS3MultipartSink, } from './multipart-uploader';
import { HttpRecordingUploadClient } from './upload-client';
export const selectRecordingSink = (params) => __awaiter(void 0, void 0, void 0, function* () {
    var _a, _b, _c;
    const legacy = () => new LegacyWebSocketSink(params.legacy);
    if (!shouldUseMultipart(params.flags)) {
        return { sink: legacy(), transport: 'legacy-websocket' };
    }
    // Every multipart endpoint is token-authorised. Without one there is nothing
    // to try, and guessing would just produce a 403 per part.
    if (!params.token) {
        return {
            sink: legacy(),
            transport: 'legacy-websocket',
            fellBackReason: 'no interview token available for the upload control plane',
        };
    }
    const client = new HttpRecordingUploadClient({
        apiBaseUrl: params.apiBaseUrl,
        slug: params.slug,
        token: params.token,
        container: (_a = params.container) !== null && _a !== void 0 ? _a : 'webm',
        elapsedSec: params.elapsedSec,
    });
    // VA-28098: scoped to the slug so two interviews in one browser profile can
    // never recover into each other's upload. Creating it never throws — a
    // browser with storage blocked gets an inert one and simply records the way
    // it did before this existed.
    const store = createPendingChunkStore(params.slug);
    const made = yield createS3MultipartSink(client, params.events, {}, store);
    if (!made) {
        return {
            sink: legacy(),
            transport: 'legacy-websocket',
            fellBackReason: 'the upload control plane would not start a multipart upload',
        };
    }
    if (made.recovered && made.recovered.parts > 0) {
        // Worth saying out loud: it means this page load followed one that died
        // holding recorded video, and that video is now in S3 rather than gone.
        (_c = (_b = params.events) === null || _b === void 0 ? void 0 : _b.onDegraded) === null || _c === void 0 ? void 0 : _c.call(_b, `restored ${made.recovered.parts} recording part(s) (${made.recovered.bytes} bytes) from the previous page`);
    }
    return {
        sink: made.sink,
        transport: 's3-multipart',
        uploadId: made.uploadId,
        recovered: made.recovered,
    };
});
/** Type guard so a context can call `finalize(duration)` only where it exists. */
export const isMultipartSink = (sink) => sink instanceof S3MultipartSink;
